import { Checkbox, IconButton, TooltipHost } from "@fluentui/react";
import { ColumnOptionsButton } from "library/greco-components";
import { ClientFilter } from "components/ClientFilter";
import { CoveragesFilterSelect } from "components/CoveragesFilterSelect";
import { FleetCountryFilter } from "components/FleetCountryFilter";
import { TlpFilter } from "components/TlpFilter";
import { useGetClientList } from "hooks/data/queries/useGetClientList";
import { ExportExcelButton } from "pages/fleet/navigation/ExportExcelButton";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  setSelectedClients,
  setSelectedCountryFilter,
  setSelectedMainIds,
} from "store/filter";
import { ResultsScore } from "../../../components/ResultsScore";
import { BackButton } from "../../../components/navigation/BackButton";
import { Links } from "../../../components/navigation/Links";
import {
  Flex,
  FlexRight,
  HSpace,
  NavigationBar,
} from "../../../components/styled";
import { useCurrentRoute } from "../../../config/routes";
import { useSelector } from "../../../store/hooks";
import {
  loadVehiclesActions,
  setCoveragesFilter,
  setIsColumnOptionsPanelOpen,
  setTlpFilter,
  setVehicleAttentionFilter,
} from "../../../store/requireActionVehicles";
import { Search } from "./Search";

type Props = {
  refExcelExport?: any;
  fleetCountryOptions: any[];
};

export const Navigation = ({ refExcelExport, fleetCountryOptions }: Props) => {
  const { t } = useTranslation();
  const route = useCurrentRoute();
  const { data: clientList } = useGetClientList();

  const selectedCountryFilter = useSelector(
    (s) => s.filter.selectedCountryFilter
  );

  const vehicleAttentionFilter = useSelector(
    (s) => s.requireActionVehicles.vehicleAttentionFilter
  );

  const userCountryCode = useSelector((s) => s.auth.userCountryCode);

  useEffect(() => {
    if (selectedCountryFilter) return;
    if (
      userCountryCode &&
      userCountryCode !== "-1" &&
      fleetCountryOptions.length > 0
    ) {
      const userCountryOption = fleetCountryOptions.find(
        (item) => item.key === userCountryCode
      );
      if (userCountryOption) {
        dispatch(setSelectedCountryFilter(userCountryCode));
      } else {
        dispatch(setSelectedCountryFilter("0"));
      }
    }
    if (
      (!userCountryCode || userCountryCode === "-1") &&
      fleetCountryOptions.length > 0
    ) {
      dispatch(setSelectedCountryFilter(fleetCountryOptions[0].key));
    }
  }, [userCountryCode, fleetCountryOptions]);

  const tlpFilter = useSelector((s) => s.requireActionVehicles.tlpFilter);
  const coveragesFilter = useSelector(
    (s) => s.requireActionVehicles.coveragesFilter
  );

  const vehiclesTotalCount = useSelector(
    (s) => s.requireActionVehicles.vehiclesTotalCount
  );
  const areVehiclesLoading = useSelector(
    (s) => s.requireActionVehicles.loadVehiclesStatus === "loading"
  );
  const dispatch = useDispatch();
  const isRequireActionVehicles = route === "requireActionVehicles";

  const selectedClients = useSelector((s) => s.filter.selectedClients);

  const clientsTooltip = selectedClients
    ? selectedClients
        .map(
          (clId) =>
            clientList?.find((cl) => cl.clientInternalNumber === clId)
              .clientName
        )
        .join(", ")
    : null;
  return (
    <>
      <NavigationBar>
        <HSpace />
        <HSpace />
        {!isRequireActionVehicles && <BackButton />}
        <Links />
        <HSpace />
        {refExcelExport && (
          <ExportExcelButton
            refExcelExport={refExcelExport}
            isFleetPage={false}
          />
        )}
      </NavigationBar>

      <NavigationBar>
        {fleetCountryOptions.length > 1 && (
          <>
            <HSpace width={10} />
            <FleetCountryFilter
              value={selectedCountryFilter}
              onChange={(val) => {
                dispatch(setSelectedCountryFilter(val.key));
              }}
              options={fleetCountryOptions}
            />
          </>
        )}
        <HSpace width={10} />
        <TooltipHost content={t("Refresh")}>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            onClick={() => {
              dispatch(
                loadVehiclesActions.trigger({
                  isReset: true,
                  shouldDelay: false,
                  shouldEmptyVehicles: true,
                })
              );
            }}
            disabled={areVehiclesLoading}
          />
        </TooltipHost>

        <HSpace />
        <Search />
        <HSpace width={20} />
        <Checkbox
          label={t("bfm.needsAttention")}
          checked={vehicleAttentionFilter}
          onChange={() =>
            dispatch(setVehicleAttentionFilter(!vehicleAttentionFilter))
          }
        />
        <HSpace width={20} />
        <TlpFilter
          tlpValue={tlpFilter}
          onChange={(val) => {
            dispatch(setTlpFilter(val.key));
          }}
        />
        <HSpace width={20} />
        <CoveragesFilterSelect
          value={coveragesFilter}
          onChange={(val) => {
            dispatch(setCoveragesFilter(val));
          }}
        />
        <HSpace width={20} />
        <TooltipHost
          content={clientsTooltip}
          styles={{
            root: {
              marginTop: "4px",
            },
          }}
        >
          <ClientFilter />
        </TooltipHost>
        {selectedClients && selectedClients.length > 0 ? (
          <>
            <HSpace />
            <TooltipHost content={"Remove filter"}>
              <IconButton
                iconProps={{ iconName: "ChromeClose" }}
                onClick={() => {
                  dispatch(setSelectedClients([]));
                  dispatch(setSelectedMainIds([]));
                }}
              />
            </TooltipHost>
          </>
        ) : null}

        <FlexRight>
          <Flex>
            {((selectedCountryFilter !== null &&
              selectedCountryFilter !== "0") ||
              fleetCountryOptions.length === 1) && (
              <>
                <ColumnOptionsButton
                  t={t}
                  isActive={false}
                  onClick={() => {
                    dispatch(setIsColumnOptionsPanelOpen(true));
                  }}
                />
                <HSpace />
              </>
            )}
            <ResultsScore score={vehiclesTotalCount} />
          </Flex>
        </FlexRight>
      </NavigationBar>
    </>
  );
};
